import { useQuery } from '@tanstack/react-query';
import type { Geometry } from 'geojson';
import { api } from 'lib/request/api';
import type { Piece } from 'components/geometry-editing';

/** Query-key prefix for a unit's persisted boundary geometry. */
export const UNIT_BOUNDARY_QUERY_KEY =
  'unit-builder:boundary-geometry' as const;

/** What the builder needs off the development area's current-version payload. */
interface DevelopmentAreaVersion {
  geometry?: Geometry | null;
  /**
   * The unit's stored GIS acreage — the same figure the development-area page
   * shows as "Gross Acres (GIS)". Rendered by
   * `development_areas/versions/show.json.jbuilder`. A decimal, so it can arrive
   * as a JSON string; coerced below.
   */
  acreage?: number | string | null;
}

/** The unit's boundary and its stored acreage, as the builder consumes them. */
export interface UnitBoundary {
  /** The true server footprint's outer ring, or null while unresolved. */
  ring: Piece | null;
  /** Stored GIS acreage, or null when the payload carried none. */
  acreage: number | null;
}

const EMPTY_BOUNDARY: UnitBoundary = { ring: null, acreage: null };

/**
 * The payload's acreage as a finite number, or null.
 *
 * @param {number | string | null | undefined} value - The raw acreage.
 * @return {number | null} The parsed acreage.
 */
const parseAcreage = (
  value: number | string | null | undefined
): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The outer ring of a persisted unit geometry, as the shape editor's `Piece`.
 *
 * A unit is a single Polygon on the server, so its outer ring is the footprint;
 * a MultiPolygon (rare) falls back to its first polygon's outer ring. Holes are
 * dropped — units don't carry them. Returns null for anything else.
 *
 * @param {Geometry | null | undefined} geometry - The development area's geometry.
 * @return {Piece | null} The outer `[lng, lat]` ring, or null.
 */
const outerRing = (geometry: Geometry | null | undefined): Piece | null => {
  if (!geometry) return null;
  if (geometry.type === 'Polygon') return geometry.coordinates[0] ?? null;
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates[0]?.[0] ?? null;
  }
  return null;
};

/**
 * The scoped unit's TRUE footprint, from
 * `GET /development_areas/:id/versions/current`.
 *
 * The map layer can only reconstruct a unit as a rotated bounding rectangle
 * (centroid + azimuth + length + width — `buildRingFromFrame`), which overshoots
 * any unit whose real shape isn't that rectangle. The server holds the exact
 * polygon, so the Unit Builder seeds its editable boundary from this instead,
 * and the drawn shape hugs the unit the map draws.
 *
 * The current-version read is used rather than the plain `GET
 * /development_areas/:id`: that action also `respond_to :html`, so a request
 * that doesn't pin the JSON format renders the heavy dev-area page and times
 * out; the versions controller is JSON-only, so it needs no `.json` suffix and
 * matches the app's own `current_version` href.
 *
 * Only a real, persisted unit has server geometry. A session clone has no id of
 * its own, so callers pass its `sourceDevelopmentAreaId` — that resolves the unit
 * it was copied from (same query key, so usually a cache hit) and gives the clone
 * the source's stored acreage. A brand-new unit passes null.
 *
 * For a REAL unit this is the only accepted boundary — the caller does NOT fall
 * back to the frame rectangle, because editing the rectangle would silently
 * reshape the saved unit. So null here means "not yet": the builder holds the
 * boundary (and shows a loading state) until this resolves.
 *
 * Also carries the unit's stored GIS acreage from the same payload, so the
 * builder can show the DB figure rather than a client measurement of the ring
 * (the two differ — the client maths is a display approximation).
 *
 * @param {number | null} unitId - The scoped unit, or null for a draft/clone.
 * @return {UnitBoundary} The unit's outer ring + stored acreage; ring null while
 *   unresolved.
 */
export const useUnitBoundaryGeometry = (
  unitId: number | null
): UnitBoundary => {
  const { data } = useQuery<UnitBoundary, Error>({
    queryKey: [UNIT_BOUNDARY_QUERY_KEY, unitId],
    queryFn: async ({ signal }) => {
      const payload = await api
        .get(`development_areas/${unitId}/versions/current`, { signal })
        .json<DevelopmentAreaVersion>();
      return {
        ring: outerRing(payload.geometry),
        acreage: parseAcreage(payload.acreage),
      };
    },
    enabled: unitId !== null,
    staleTime: Infinity,
    // No retry: retries multiply one slow read into several against the same
    // worker. A real unit has no frame-rectangle fallback (editing the rectangle
    // would reshape the saved unit), so a failed read means the builder keeps
    // waiting rather than showing a wrong boundary.
    retry: false,
  });

  return data ?? EMPTY_BOUNDARY;
};
